import { PriorityHighRounded as PriorityHighRoundedIcon } from 'components/icons';

import TabsGallery, { TabType } from 'components/TabsGallery';
import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoiceStatusType, InvoiceType, PrepaymentDataListItem } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { DetailsTabSuccessMessages } from '../InvoicePage';
import { AddNewInvoiceItems } from '../hooks/useAddNewInvoiceItems';
import { useInvoiceDetails } from '../hooks/useInvoiceDetails';
import { useInvoiceFlow } from '../hooks/useInvoiceFlow';
import General from './views/General';
import Details from './views/details-tab/Details';
import Totals from './views/totals-tab/Totals';

// Padding scales with the width of the tab pane (each pane is a CSS container via
// `@container` in its tab className below), not the viewport — so opening
// the Summary drawer tightens the paddings too.
const CenterBlockContentWrap = ({
    isCorrectionInvoice,
    isSplitInvoice,
    className,
    children,
}: {
    isCorrectionInvoice?: boolean;
    isSplitInvoice?: boolean;
    className?: string;
    children: ReactNode;
}) => (
    <div
        className={cn(
            'new:gap-(--card-gap) flex h-full flex-col gap-6 overflow-y-auto px-12 py-[22px] transition-[padding] duration-[400ms] ease-in-out',
            '@max-5xl:px-6 @max-5xl:py-3.5 @max-3xl:px-4 @max-3xl:py-2',
            isCorrectionInvoice
                ? 'new:bg-changed bg-[rgba(255,244,229,0.5)]'
                : isSplitInvoice
                  ? 'new:bg-info-lighter bg-[rgba(237,231,246,0.5)]'
                  : 'bg-grey-50',
            className
        )}
    >
        {children}
    </div>
);

function TabAlertIcon({ color }: { color: 'error' | 'warning' }) {
    return (
        <div
            className={cn(
                'flex size-6 items-center justify-center rounded-sm',
                color === 'error' ? 'bg-error-light' : 'bg-warning-light'
            )}
        >
            <PriorityHighRoundedIcon className={cn('text-[14px]', color === 'error' ? 'text-error' : 'text-warning')} />
        </div>
    );
}

interface Props {
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    addedInvoiceTimecardIds: number[];
    addedInvoiceExpenseIds: number[];
    setAddedInvoiceTimecardIds: Dispatch<SetStateAction<number[]>>;
    setAddedInvoiceExpenseIds: Dispatch<SetStateAction<number[]>>;
    successMessages: DetailsTabSuccessMessages;
    setSuccessMessages: Dispatch<SetStateAction<DetailsTabSuccessMessages>>;
    handleStatusLoadingState: (isLoading: boolean) => void;
    addNewInvoiceItems: AddNewInvoiceItems;
    isLoading: boolean;
    searchExistingPrepaymentsText: string;
    setSearchExistingPrepaymentsText: (value: string) => void;
    submitInvoice: () => void;
    submitTemplateUpdate: () => void;
    isTemplateEditable: boolean;
    updateInvoiceLimited: () => void;
    existingPrepayments?: PrepaymentDataListItem[];
}

const CenterBlock = ({
    invoice,
    classifiers,
    isInViewMode,
    addedInvoiceTimecardIds,
    addedInvoiceExpenseIds,
    setAddedInvoiceTimecardIds,
    setAddedInvoiceExpenseIds,
    successMessages,
    setSuccessMessages,
    handleStatusLoadingState,
    addNewInvoiceItems,
    isLoading,
    searchExistingPrepaymentsText,
    setSearchExistingPrepaymentsText,
    submitInvoice,
    submitTemplateUpdate,
    isTemplateEditable,
    updateInvoiceLimited,
    existingPrepayments,
}: Props) => {
    const isCorrectionInvoice = invoice.invoiceTypeCode === InvoiceType.CORRECTION;
    const isSplitInvoice = invoice.isSplit ?? false;

    const hasErrors =
        invoice.invoiceTypeCode === InvoiceType.DEBIT &&
        !isSplitInvoice &&
        (invoice.invoiceMatters?.some((item) => item.hasMatterCapError) ||
            invoice.invoiceMatters?.some((item) => item.hasUnsignedServiceAgreement));

    const { isDetailsReadOnly, hasDetailAlerts } = useInvoiceDetails(invoice);
    const { hasFlowAlerts } = useInvoiceFlow(invoice);

    const initialActiveTab = useMemo(() => {
        if (
            (invoice.invoiceTypeCode === InvoiceType.DEBIT || isCorrectionInvoice) &&
            (invoice.invoiceStatusTypeCode === InvoiceStatusType.DRAFT ||
                invoice.invoiceStatusTypeCode === InvoiceStatusType.REVIEWED ||
                invoice.invoiceStatusTypeCode === InvoiceStatusType.APPROVED)
        ) {
            return 1;
        }
        return 0;
    }, [invoice.invoiceTypeCode, invoice.invoiceStatusTypeCode, isCorrectionInvoice]);

    const tabComponents = useMemo((): TabType[] => {
        return [
            {
                id: 'general',
                label: (
                    <div className="flex items-center gap-2">
                        <FormattedMessage id="invoices.general" />
                        {hasFlowAlerts ? <TabAlertIcon color="warning" /> : null}
                    </div>
                ),
                className: '@container overflow-hidden',
                component: (
                    <CenterBlockContentWrap isCorrectionInvoice={isCorrectionInvoice} isSplitInvoice={isSplitInvoice}>
                        <General
                            invoice={invoice}
                            classifiers={classifiers}
                            isInViewMode={isInViewMode}
                            handleStatusLoadingState={handleStatusLoadingState}
                            isLoading={isLoading}
                            searchExistingPrepaymentsText={searchExistingPrepaymentsText}
                            setSearchExistingPrepaymentsText={setSearchExistingPrepaymentsText}
                            submitInvoice={submitInvoice}
                            submitTemplateUpdate={submitTemplateUpdate}
                            isTemplateEditable={isTemplateEditable}
                            updateInvoiceLimited={updateInvoiceLimited}
                            existingPrepayments={existingPrepayments}
                        />
                    </CenterBlockContentWrap>
                ),
            },
            ...(invoice.invoiceTypeCode !== InvoiceType.PREPAYMENT
                ? [
                      {
                          id: 'details',
                          label: (
                              <div className="flex items-center gap-2">
                                  <FormattedMessage id="invoices.details.title" />
                                  {hasErrors ? <TabAlertIcon color="error" /> : null}
                                  {hasDetailAlerts && !isInViewMode ? <TabAlertIcon color="warning" /> : null}
                              </div>
                          ),
                          className: '@container overflow-auto',
                          component: (
                              <CenterBlockContentWrap
                                  isCorrectionInvoice={isCorrectionInvoice}
                                  isSplitInvoice={isSplitInvoice}
                                  className="min-w-[600px]"
                              >
                                  <Details
                                      invoice={invoice}
                                      classifiers={classifiers}
                                      isInViewMode={isInViewMode || isDetailsReadOnly}
                                      handleStatusLoadingState={handleStatusLoadingState}
                                      addNewInvoiceItems={addNewInvoiceItems}
                                      addedInvoiceTimecardIds={addedInvoiceTimecardIds}
                                      addedInvoiceExpenseIds={addedInvoiceExpenseIds}
                                      setAddedInvoiceTimecardIds={setAddedInvoiceTimecardIds}
                                      setAddedInvoiceExpenseIds={setAddedInvoiceExpenseIds}
                                      successMessages={successMessages}
                                      setSuccessMessages={setSuccessMessages}
                                      isLoading={isLoading}
                                  />
                              </CenterBlockContentWrap>
                          ),
                      },
                  ]
                : []),
            {
                id: 'totals',
                label: t('invoices.totals-tab.title'),
                className: '@container overflow-hidden',
                component: (
                    <CenterBlockContentWrap isCorrectionInvoice={isCorrectionInvoice} isSplitInvoice={isSplitInvoice}>
                        <Totals
                            isLoading={isLoading}
                            invoice={invoice}
                            classifiers={classifiers}
                            isInViewMode={isInViewMode}
                        />
                    </CenterBlockContentWrap>
                ),
            },
        ];
    }, [
        invoice,
        classifiers,
        isInViewMode,
        handleStatusLoadingState,
        addNewInvoiceItems,
        isLoading,
        addedInvoiceTimecardIds,
        setAddedInvoiceTimecardIds,
        addedInvoiceExpenseIds,
        setAddedInvoiceExpenseIds,
        successMessages,
        setSuccessMessages,
        searchExistingPrepaymentsText,
        setSearchExistingPrepaymentsText,
        isDetailsReadOnly,
        hasDetailAlerts,
        hasFlowAlerts,
        hasErrors,
        submitInvoice,
        updateInvoiceLimited,
        existingPrepayments,
        isCorrectionInvoice,
        isSplitInvoice,
        submitTemplateUpdate,
        isTemplateEditable,
    ]);

    return (
        <TabsGallery
            tabs={tabComponents}
            className="m-0 px-3"
            wrapperClassName="grid flex-1 grid-rows-[auto_1fr] overflow-hidden"
            tabGroupId="invoices-center-block"
            trackTabsInHistoryState
            usePathBasedRouting
            initialActiveTab={initialActiveTab}
        />
    );
};

export default CenterBlock;
